/**
 * Producto: galería con miniaturas y zoom, selector de variantes,
 * cantidad y barra fija de compra en el móvil.
 */
import { theme, debounce, formatMoney, prefersReducedMotion } from '@theme/global';

/* Galería ------------------------------------------------------------------ */

class ProductGallery extends HTMLElement {
  connectedCallback() {
    this.sectionId = this.dataset.section;
    this.slides = this.querySelector('[data-gallery-slides]');
    this.thumbs = [...this.querySelectorAll('[data-thumb-for]')];
    this.counter = this.querySelector('[data-gallery-counter]');
    this.zoomDialog = this.querySelector('[data-zoom-dialog]');

    this.onVariantChange = this.onVariantChange.bind(this);
    document.addEventListener('variant:change', this.onVariantChange);

    if (!this.slides) return;

    this.thumbs.forEach((thumb) => {
      thumb.addEventListener('click', () => this.goTo(thumb.dataset.thumbFor));
    });

    this.slides.addEventListener(
      'scroll',
      debounce(() => this.syncFromScroll(), 60),
      { passive: true }
    );

    this.slides.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowRight') this.step(1);
      else if (event.key === 'ArrowLeft') this.step(-1);
      else return;
      event.preventDefault();
    });

    const active = this.slides.querySelector('.is-active');
    if (active && active !== this.slides.firstElementChild) this.goTo(active.dataset.mediaId, false);

    if (this.zoomDialog) this.initZoom();
  }

  disconnectedCallback() {
    document.removeEventListener('variant:change', this.onVariantChange);
  }

  get slideList() {
    return [...this.slides.children];
  }

  step(direction) {
    const list = this.slideList;
    const index = list.findIndex((slide) => slide.classList.contains('is-active'));
    const next = list[Math.min(Math.max(index + direction, 0), list.length - 1)];
    if (next) this.goTo(next.dataset.mediaId);
  }

  goTo(mediaId, smooth = true) {
    const list = this.slideList;
    const index = list.findIndex((slide) => slide.dataset.mediaId === String(mediaId));
    if (index < 0) return;
    this.slides.scrollTo({
      left: index * this.slides.clientWidth,
      behavior: smooth && !prefersReducedMotion() ? 'smooth' : 'auto',
    });
    this.setActive(index);
  }

  syncFromScroll() {
    const index = Math.round(this.slides.scrollLeft / this.slides.clientWidth);
    this.setActive(index);
  }

  setActive(index) {
    const list = this.slideList;
    list.forEach((slide, i) => {
      const isActive = i === index;
      slide.classList.toggle('is-active', isActive);
      if (!isActive) slide.querySelectorAll('video').forEach((video) => video.pause());
    });
    const mediaId = list[index]?.dataset.mediaId;
    this.thumbs.forEach((thumb) => {
      if (thumb.dataset.thumbFor === mediaId) {
        thumb.setAttribute('aria-current', 'true');
        const container = thumb.closest('.gallery__thumbs');
        const thumbRect = thumb.getBoundingClientRect();
        const containerRect = container.getBoundingClientRect();
        if (thumbRect.left < containerRect.left || thumbRect.right > containerRect.right) {
          container.scrollLeft += thumbRect.left - containerRect.left - (containerRect.width - thumbRect.width) / 2;
        }
      } else {
        thumb.removeAttribute('aria-current');
      }
    });
    if (this.counter) this.counter.textContent = `${index + 1} / ${list.length}`;
  }

  onVariantChange(event) {
    const { sectionId, variant } = event.detail;
    if (sectionId !== this.sectionId || !variant?.featured_media) return;
    this.goTo(variant.featured_media.id);
  }

  initZoom() {
    const stage = this.zoomDialog.querySelector('[data-zoom-stage]');
    const image = this.zoomDialog.querySelector('[data-zoom-image]');

    this.addEventListener('click', (event) => {
      const trigger = event.target.closest('[data-zoom-src]');
      if (!trigger) return;
      const original = trigger.querySelector('img');
      image.src = trigger.dataset.zoomSrc;
      image.alt = original?.alt || '';
      stage.classList.remove('is-zoomed');
      this.zoomDialog.showModal();
    });

    stage.addEventListener('click', (event) => {
      if (event.target.closest('button')) return;
      const zoomed = stage.classList.toggle('is-zoomed');
      if (zoomed) this.moveZoom(event, image);
    });

    stage.addEventListener('pointermove', (event) => {
      if (stage.classList.contains('is-zoomed')) this.moveZoom(event, image);
    });
  }

  moveZoom(event, image) {
    const rect = image.getBoundingClientRect();
    const x = ((event.clientX - rect.left) / rect.width) * 100;
    const y = ((event.clientY - rect.top) / rect.height) * 100;
    image.style.transformOrigin = `${Math.min(Math.max(x, 0), 100)}% ${Math.min(Math.max(y, 0), 100)}%`;
  }
}

/* Precio ------------------------------------------------------------------- */

function renderPrice(priceElement, variant) {
  if (!priceElement || !variant) return;
  const onSale = variant.compare_at_price > variant.price;
  const { salePrice, regularPrice } = theme.strings;
  priceElement.classList.toggle('price--on-sale', onSale);
  priceElement.innerHTML =
    `<span class="visually-hidden">${onSale ? salePrice : regularPrice}</span>` +
    `<span class="price__current">${formatMoney(variant.price)}</span>` +
    (onSale
      ? `<span class="visually-hidden">${regularPrice}</span><s class="price__compare">${formatMoney(variant.compare_at_price)}</s>`
      : '');
}

function buttonLabel(variant) {
  if (!variant) return theme.strings.unavailable;
  return variant.available ? theme.strings.addToCart : theme.strings.soldOut;
}

/* Selector de variantes ---------------------------------------------------- */

class VariantPicker extends HTMLElement {
  connectedCallback() {
    this.sectionId = this.dataset.section;
    const data = this.querySelector('[data-variants]');
    this.variants = data ? JSON.parse(data.textContent) : [];
    this.addEventListener('change', () => this.onChange());
    this.updateAvailability();
  }

  get fieldsets() {
    return [...this.querySelectorAll('fieldset')];
  }

  get selectedOptions() {
    return this.fieldsets.map((fieldset) => fieldset.querySelector('input:checked')?.value);
  }

  onChange() {
    const selected = this.selectedOptions;
    const variant = this.variants.find((item) => item.options.every((option, i) => option === selected[i]));

    this.fieldsets.forEach((fieldset, i) => {
      const label = fieldset.querySelector('[data-selected-value]');
      if (label) label.textContent = selected[i] || '';
    });
    this.updateAvailability();

    const form = document.getElementById(`ProductForm-${this.sectionId}`);
    const input = form?.querySelector('[data-variant-input]');
    if (input && variant) input.value = variant.id;

    const addButtons = [
      form?.querySelector('[data-add-button]'),
      document.querySelector(`sticky-buy-bar[data-section="${this.sectionId}"] [data-sticky-add]`),
    ].filter(Boolean);
    addButtons.forEach((button) => {
      button.disabled = !variant || !variant.available;
      const text = button.querySelector('[data-add-text]') || button;
      text.textContent = buttonLabel(variant);
    });

    document
      .querySelectorAll(
        `[data-price-container="${this.sectionId}"] [data-price], sticky-buy-bar[data-section="${this.sectionId}"] [data-price]`
      )
      .forEach((price) => renderPrice(price, variant));

    if (variant && this.hasAttribute('data-update-url')) {
      const url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url.toString());
    }

    document.dispatchEvent(
      new CustomEvent('variant:change', { detail: { sectionId: this.sectionId, variant } })
    );
  }

  /** Marca los valores que no tienen ninguna variante disponible con la selección actual. */
  updateAvailability() {
    const selected = this.selectedOptions;
    this.fieldsets.forEach((fieldset, position) => {
      fieldset.querySelectorAll('input[type="radio"]').forEach((input) => {
        const available = this.variants.some(
          (variant) =>
            variant.available &&
            variant.options[position] === input.value &&
            variant.options.every((option, i) => i === position || option === selected[i])
        );
        const label = input.nextElementSibling;
        if (!label) return;
        label.classList.toggle('is-unavailable', !available);
        const hint = label.querySelector('[data-unavailable-label]');
        if (hint) hint.hidden = available;
      });
    });
  }
}

/* Cantidad ----------------------------------------------------------------- */

class QuantityInput extends HTMLElement {
  connectedCallback() {
    this.input = this.querySelector('input');
    this.addEventListener('click', (event) => {
      const button = event.target.closest('button');
      if (!button || !this.input) return;
      const current = parseInt(this.input.value, 10) || 1;
      const next = button.name === 'plus' ? current + 1 : Math.max(1, current - 1);
      this.input.value = next;
      this.input.dispatchEvent(new Event('change', { bubbles: true }));
    });
  }
}

/* Barra fija de compra ----------------------------------------------------- */

class StickyBuyBar extends HTMLElement {
  connectedCallback() {
    const section = this.closest('[data-product-section]');
    const target = section?.querySelector('[data-main-buy]');
    const button = this.querySelector('[data-sticky-add]');
    if (!target || !('IntersectionObserver' in window)) return;

    this.observer = new IntersectionObserver(([entry]) => {
      const show = !entry.isIntersecting && entry.boundingClientRect.top < 0;
      this.classList.toggle('is-visible', show);
      this.setAttribute('aria-hidden', String(!show));
      if (button) button.tabIndex = show ? 0 : -1;
    });
    this.observer.observe(target);
  }

  disconnectedCallback() {
    this.observer?.disconnect();
  }
}

const define = (name, element) => {
  if (!customElements.get(name)) customElements.define(name, element);
};

define('product-gallery', ProductGallery);
define('variant-picker', VariantPicker);
define('quantity-input', QuantityInput);
define('sticky-buy-bar', StickyBuyBar);
